/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import useAPI from '../../../../utils/api-manager/Helper/useApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DatePicker, Space, Typography, Empty } from 'antd';

const { RangePicker } = DatePicker;
const { Title: AntdTitle } = Typography;

export default function StackedBarChart() {
  const [dateRange, setDateRange] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [, setLoading] = useState(false);

  const { Post } = useAPI();

  const fetchDashboardData = async (startDate, endDate) => {
    setLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
      };

      const response = await Post('dashboardChart', payload);
      const data = response.data;

      const businessReports = data.business_reports || [];
      const lotteryReports = data.lottery_reports || [];

      const mappedData = businessReports.map((entry, index) => ({
        name: entry.date,
        outflow: parseFloat(entry.cash_to_account || 0),
        inflow: parseFloat(lotteryReports[index]?.total_balance || 0),
      }));

      const filtered = mappedData.filter(
        (entry) => new Date(entry.name) >= new Date(startDate) && new Date(entry.name) <= new Date(endDate)
      );

      if (filtered.length === 0) {
        setFilteredData([]);
      } else {
        setFilteredData(filtered);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (!dates || dates.length === 0) {
      setFilteredData([]);
    } else if (dates.length === 2) {
      const [start, end] = dates;
      fetchDashboardData(start.format('YYYY-MM-DD'))
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      fetchDashboardData(start.format('YYYY-MM-DD'));
    }
  }, [dateRange]);

  const netCashFlow = filteredData.reduce((acc, curr) => acc + curr.outflow + curr.inflow, 0);
  const formattedCashFlow = netCashFlow.toLocaleString();
  const totalOutflow = filteredData.reduce((acc, curr) => acc + curr.inflow, 0);
  const profitPercentage = totalOutflow !== 0 ? ((netCashFlow / totalOutflow) * 100).toFixed(2) : 0;
  const arrow = profitPercentage > 0 ? '↗' : profitPercentage < 0 ? '↓' : '↘';
  const arrowColor = profitPercentage > 0 ? 'green' : profitPercentage < 0 ? 'red' : 'gray';

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: '0 0 5px 0' }}>{label}</p>
          <p style={{ margin: '0', color: 'blue' }}>Business Report: ${payload[0].value}</p>
          <p style={{ margin: '0', color: 'black' }}>Lottery: ${Math.abs(payload[1].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '20px', width: '1050px', margin: '20px 153px', backgroundColor: 'white', borderRadius: '10px', height: "520px" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <AntdTitle level={2} style={{ fontSize: '20px', marginBottom: '10px' }}>
            Cashflow:
          </AntdTitle>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <span style={{ color: netCashFlow >= 0 ? 'black' : 'green', fontSize: '30px', marginRight: '10px' }}>
              ${formattedCashFlow}
            </span>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: arrowColor }}>
              <span style={{ fontSize: '16px' }}>{arrow}</span>
              {profitPercentage}%
            </span>
          </div>
        </div>
        <Space>
          <RangePicker onChange={handleDateChange} />
        </Space>
      </div>
      {filteredData.length > 0 ? (
        <div style={{ position: 'relative', width: '80%', height: '400px', margin: '0 auto' }}>
          <div style={{ position: 'absolute', top: '-5%', left: '53%', transform: 'translateX(-50%)', fontWeight: 'bold', fontSize: '16px', color: 'blue' }}>
            Business Report
          </div>
          <div style={{ position: 'absolute', bottom: '-3%', left: '53%', transform: 'translateX(-50%)', fontWeight: 'bold', fontSize: '16px', color: 'black' }}>
            Lottery
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="outflow"
                name="Business Report"
                fill="url(#businessGradient)"
                stackId="stack"
              >
                <defs>
                  <linearGradient id="businessGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="2%" stopColor="rgba(0, 0, 255, 0.4)" />
                    <stop offset="90%" stopColor="rgba(0, 0, 255, 0)" />
                  </linearGradient>
                </defs>
              </Bar>
              <Bar
                dataKey="inflow"
                name="Lottery"
                fill="url(#lotteryGradient)"
                stackId="stack"
              >
                <defs>
                  <linearGradient id="lotteryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(160, 160, 160, 0.2)" />
                    <stop offset="95%" stopColor="rgba(160, 160, 160, 0.2)" />
                  </linearGradient>
                </defs>
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
          }}
        >
          <Empty description="No data available for the selected date range" />
        </div>
      )}
    </div>
  );
}
